import React, { useState, useRef } from 'react'
import Icon from '@components/icons'
import Tooltip from '@components/tooltip'
import notification from '@components/notification'
import { Line } from 'rc-progress'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import {
  getInvoiceAttachmentDownloadUrl,
  getPaymentLinkAttachmentDownloadUrl
} from '@app/containers/payment/invoice-attachment-service'
import style from './InvoiceAttachments.module.scss'

/**
 * Invoice Attachment Component for Send Invoice Popup
 * Uses the centralized attachment API instead of the old estimate-specific endpoint
 */
const SendInvoiceAttachments = ({
  uploadingAttachments = [],
  uploadedAttachments = [],
  onCancelUploadingAttachment = () => {},
  onClearUploadingAttachments = () => {},
  onClearUploadedAttachments = () => {},
  onRemoveUploadedAttachment = () => {},
  uploadInvoiceAttachments = () => {},
  maxFileCount = 5,
  module = 'invoices' // 'invoices' or 'getpaid'
}) => {
  const { t } = useTranslation()
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef(null)

  const handleDrag = function (e) {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  function formatBytes(inputBytes, decimalPlaces = 2) {
    if (!+inputBytes) return '0 Bytes'
    const numOfDecimalPlaces = decimalPlaces < 0 ? 0 : decimalPlaces
    const numOfBytes = Math.floor(Math.log(inputBytes) / Math.log(1024))
    return `${parseFloat((inputBytes / Math.pow(1024, numOfBytes)).toFixed(numOfDecimalPlaces))} ${
      ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'][numOfBytes]
    }`
  }

  function formatDate(timeStamp) {
    return moment(timeStamp).format('D MMM, YYYY')
  }

  function cancelFileUpload(fileId) {
    onCancelUploadingAttachment(fileId)
  }

  function deleteFile(fileId) {
    onRemoveUploadedAttachment(fileId)
  }

  const handleDrop = async function (e) {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const files = Array.from(e.dataTransfer.files || [])
    uploadFiles(files)
  }

  const handleChange = async function (e) {
    e.preventDefault()
    const files = Array.from(e.target.files || [])
    uploadFiles(files)
    // Clear the input so the same file can be selected again if needed
    e.target.value = ''
  }

  const removeSpecialChars = (files) => {
    return files.map((file) => renameFile(file, file.name.replace(/[^a-zA-Z0-9._ ]/g, '')))
  }

  const renameFile = (file, fileName) => new File([file], fileName, { type: file.type })

  const uploadFiles = async (filesProvided) => {
    const files = removeSpecialChars(filesProvided)

    // Check file count limit
    if (files.length + uploadingAttachments.length + uploadedAttachments.length > maxFileCount) {
      notification('error', t('payments.maximum-5-files-are-allowed'))
      return
    }

    // Upload all valid files (duplicate check removed - same-name files from different folders are allowed)
    if (files.length > 0) {
      uploadInvoiceAttachments({
        files
      })
    }
  }

  const onButtonClick = () => {
    inputRef.current.click()
  }

  const handleFilePreview = (file) => {
    if (!file.key && !file.s3Key) {
      notification('error', t('payments.file-preview-not-available'))
      return
    }

    const fileKey = file.key || file.s3Key
    const downloadUrl =
      module === 'getpaid' ? getPaymentLinkAttachmentDownloadUrl(fileKey) : getInvoiceAttachmentDownloadUrl(fileKey)

    // Open file in new tab for preview
    window.open(downloadUrl, '_blank')
  }

  const handleFileDownload = (file) => {
    if (!file.key && !file.s3Key) {
      notification('error', t('payments.file-download-not-available'))
      return
    }

    const fileKey = file.key || file.s3Key
    const downloadUrl =
      module === 'getpaid' ? getPaymentLinkAttachmentDownloadUrl(fileKey) : getInvoiceAttachmentDownloadUrl(fileKey)

    // Create a temporary link to download the file
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = file.name || file.originalname || file.originalName || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="col-12">
      <div className="form-group custom-form-group-dropdown mb-30 custom-cursor-hide">
        <label className="label-text" style={{ 'margin-right': '1%' }}>
          {t('payments.attachments')}
        </label>
        <Tooltip position="right" message={t('payments.you-can-add-upto-5-attachments')}>
          <Icon name="informationButton" className={style['info-button']} />
        </Tooltip>

        {uploadingAttachments.length !== 0 || uploadedAttachments.length !== 0 ? (
          <div
            className="text-primary cursor-pointer"
            onClick={onButtonClick}
            style={{ display: 'inline-block', marginLeft: '16px' }}>
            {t('payments.choose-file')}
            <input
              ref={inputRef}
              type="file"
              className={style['input-file-upload']}
              multiple={true}
              onChange={handleChange}
              accept=".doc,.docx,application/pdf, image/*"
            />
          </div>
        ) : null}

        <div>
          {uploadingAttachments.length === 0 && uploadedAttachments.length === 0 ? (
            <form
              className={style['full-form-file-upload']}
              onDragEnter={handleDrag}
              onSubmit={(e) => e.preventDefault()}>
              <input
                ref={inputRef}
                type="file"
                className={style['input-file-upload']}
                multiple={true}
                onChange={handleChange}
                accept=".doc,.docx,application/pdf,image/*"
              />
              <label
                className={dragActive ? style['label-file-upload-active'] : style['label-file-upload-updated']}
                htmlFor={style['input-file-upload']}>
                <div>
                  <Icon name="addPlus" />
                  <p className={style['drag-and-drop-text']}>{t('payments.drag-and-drop-your-files-here')}</p>
                  <p className={style['supported-file-formats']}>
                    {t('payments.supported-file-formats-are-png-jpeg-doc-and-pdf-file-size-must-be-under-10-mb')}
                  </p>
                  <div className={style['choose-file-description']} onClick={onButtonClick}>
                    {t('payments.choose-file')}
                  </div>
                </div>
              </label>
              {dragActive && (
                <div
                  className={style['drag-file-element']}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}></div>
              )}
            </form>
          ) : null}

          {uploadedAttachments.length > 0 ? (
            <div className={style['files-container']}>
              {uploadedAttachments.map((file, i) => (
                <div className={`border rounded-sm mb-15 ${style['file-item']}`} key={i} id={i}>
                  <div className={style['file-row']}>
                    <div
                      className={style['file-icon-container']}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleFilePreview(file)}
                      title={t('payments.click-to-preview-file')}>
                      {(file.s3Key && file.s3Key.includes('.pdf')) ||
                      file.contentType === 'application/pdf' ||
                      (file.name && file.name.toLowerCase().includes('.pdf')) ? (
                        <Icon name="extPdf" className={style['show-files']} />
                      ) : (file.s3Key && (file.s3Key.includes('.doc') || file.s3Key.includes('.docx'))) ||
                        (file.contentType &&
                          (file.contentType.includes('word') || file.contentType.includes('document'))) ||
                        (file.name &&
                          (file.name.toLowerCase().includes('.doc') || file.name.toLowerCase().includes('.docx'))) ? (
                        <Icon name="extDoc" className={style['show-files']} />
                      ) : (
                        <Icon name="photo" className={style['show-files']} />
                      )}
                    </div>
                    <div className={style['file-content']}>
                      <div
                        className={style['file-name']}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleFilePreview(file)}
                        title={t('payments.click-to-preview-file')}>
                        {file.name || file.originalName || file.originalname || 'Unknown file'}
                      </div>
                      <div className={style['file-details']}>
                        {file.size > 5 * 1024 * 1024 ? (
                          <span className={style['file-error']}>
                            {t('payments.file-size-should-be-less-than-10-mb')}
                          </span>
                        ) : (
                          <span className={style['file-info']}>
                            {formatBytes(file.size)} . {formatDate(file.uploadedAt || new Date())}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={style['file-actions']}>
                      <Icon
                        name="eye"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFilePreview(file)
                        }}
                        className={style['file-icon']}
                        title={t('payments.preview-file')}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      <Icon
                        name="download"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFileDownload(file)
                        }}
                        className={style['file-icon']}
                        title={t('payments.download-file')}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      <Icon
                        name="remove"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteFile(file.key || file.s3Key || file.name || file.originalName || file.originalname)
                        }}
                        className={style['file-icon']}
                        title={t('payments.remove-file')}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {uploadingAttachments.length > 0 ? (
            <div className={style['files-container']}>
              {uploadingAttachments.map((file, i) =>
                file.progress < 100 && !file.cancelUpload ? (
                  <div className={`border rounded-sm mb-15 ${style['file-item']}`} key={file.fileId || file.name}>
                    <div className={style['file-row']}>
                      <div className={style['file-icon-container']}>
                        {file.type === 'application/pdf' || (file.name && file.name.toLowerCase().includes('.pdf')) ? (
                          <Icon name="extPdf" className={style['show-files']} />
                        ) : file.type.includes('word') ||
                          file.type.includes('.doc') ||
                          file.type.includes('document') ||
                          (file.name &&
                            (file.name.toLowerCase().includes('.doc') || file.name.toLowerCase().includes('.docx'))) ? (
                          <Icon name="extDoc" className={style['show-files']} />
                        ) : (
                          <Icon name="photo" className={style['show-files']} />
                        )}
                      </div>
                      <div className={style['file-content']}>
                        <div className={style['file-name']}>{file.name}</div>
                        <div className={style['file-details']}>
                          {file.progress && file.progress !== 100 ? (
                            <div className={style['progress-container']}>
                              <Line
                                percent={file.progress}
                                className={style['progress-bar']}
                                strokeWidth="2"
                                strokeColor="#35a800"
                              />
                              <span className={style['progress-text']}>
                                {file.progress}% {t('payments.completed')}
                              </span>
                            </div>
                          ) : file.size > 5 * 1024 * 1024 ? (
                            <span className={style['file-error']}>
                              {t('payments.file-size-should-be-less-than-10-mb')}
                            </span>
                          ) : (
                            <span className={style['file-info']}>
                              {formatBytes(file.size)} . {formatDate(file.lastModified)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={style['file-actions']}>
                        {file.progress && file.progress !== 100 ? (
                          <Icon
                            name="crossThin"
                            onClick={(e) => {
                              cancelFileUpload(file.fileId || file.name)
                            }}
                            className={style['file-icon']}
                          />
                        ) : (
                          <Icon
                            name="remove"
                            onClick={(e) => {
                              cancelFileUpload(file.fileId || file.name)
                            }}
                            className={style['file-icon']}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : null
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default SendInvoiceAttachments
